import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { SiblingRelationship } from "shared/cubiq-services/lookup/model/impl/SiblingRelationshipManager";
import { TranslateService } from "@ngx-translate/core";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigIDRefHelper } from "../../../helper-services/bfx-config-id-ref-helper";
import { PricingService } from "../../lookup/pricing/PricingService";

export class StrategyTooltip {
  constructor(
    protected translate: TranslateService,
    protected pricingService: PricingService,
    private sysConfigUtil?: BfxSysConfigUtilService,
  ) {}

  getStrategyDescription(order: CubiqTS.OrderDataImpl): string {
    let siblingDesc: string = "";
    if (
      order.siblingRelationship &&
      order.siblingRelationship.relationshipType == CubiqTS.OrderRelationshipType.GAMMA
    ) {
      siblingDesc =
        CubiqTS.OrderRelationshipType[order.siblingRelationship.relationshipType] +
        "\n" +
        this.createGammaDescription(order.siblingRelationship);
    } else if (order.siblingRelationship && order.siblingRelationship.order != null) {
      siblingDesc =
        CubiqTS.OrderRelationshipType[order.siblingRelationship.relationshipType] +
        "\n" +
        this.createOrderDescription(order.siblingRelationship.order);
    } else if (order.siblingRelationship) {
      siblingDesc =
        CubiqTS.OrderRelationshipType[order.siblingRelationship.relationshipType] +
        "\n" +
        this.createOrderIdDescription(order.siblingRelationship);
    }
    let childDescription: string = "";
    if (order.childRelationships && order.childRelationships.length > 0) {
      childDescription = this.createChildrenDescription(order.childRelationships);
      childDescription = siblingDesc != "" ? "\n" + "\n" + childDescription : childDescription;
    }
    return siblingDesc + childDescription;
  }

  protected createOrderDescription(order: CubiqTS.OrderDataImpl): string {
    if (!order) return null;

    const fixedCurrency: string = order.fixedCode;
    const dealtPair: string = order.isInvertedOrderFg
      ? order.nonBaseCode + order.baseCode
      : order.baseCode + order.nonBaseCode;

    const reqOrderType: string = this.getRequestOrderTypeLabel(order);
    let currencyAndRate: string = fixedCurrency + " in " + dealtPair;

    if (!this.pricingService.isMktOrder(order))
      currencyAndRate = currencyAndRate + " @ " + this.pricingService.getDealtSpotTracking(order);

    const forOrder: string = " (" + BfxConfigIDRefHelper.getOrderRef(order) + ")";

    let amount: string;
    if (order.orderType == CubiqTS.OrderType.LIMIT_CALL || order.orderQty == null) {
      amount = " ";
    } else {
      amount = order.orderQty.toFormat(0) + " ";
    }
    const description: string =
      " - " +
      this.clientIntent(order) +
      " " +
      reqOrderType +
      " " +
      amount +
      currencyAndRate +
      forOrder;
    return description;
  }

  /**
   * Create the special description for the Mid rung of a gamma order that has no intent or amount
   * @param order
   * @returns
   */
  protected createGammaMidDescription(order: CubiqTS.OrderDataImpl): string {
    if (!order) return null;

    const dealtPair: string = order.isInvertedOrderFg
      ? order.nonBaseCode + order.baseCode
      : order.baseCode + order.nonBaseCode;

    const reqOrderType: string = this.getRequestOrderTypeLabel(order);
    const currencyAndRate: string =
      " in " + dealtPair + " @ " + this.pricingService.getDealtSpotTracking(order);

    const forOrder: string = " (" + BfxConfigIDRefHelper.getOrderRef(order) + ")";

    const description: string = " - Mid " + reqOrderType + " " + currencyAndRate + forOrder;
    return description;
  }

  protected createOrderIdDescription(relationship: CubiqTS.Relationship): string {
    return `( ${BfxConfigIDRefHelper.getOrderRef(relationship)} )`;
  }

  protected createGammaDescription(relationship: CubiqTS.Relationship): string {
    const gammaRelationship = relationship as SiblingRelationship;
    let descs: Array<string>;

    if (gammaRelationship) {
      const siblings: Array<CubiqTS.OrderDataImpl> = gammaRelationship.siblingOrders;
      siblings.sort((order1: CubiqTS.OrderDataImpl, order2: CubiqTS.OrderDataImpl) =>
        order2.requestRt.comparedTo(order1.requestRt),
      );
      descs = siblings.map((order) => {
        if (order.orderState == CubiqTS.OrderState.GAMMA_MID_PENDING) {
          return this.createGammaMidDescription(order);
        } else {
          return this.createOrderDescription(order);
        }
      });
    } else {
      descs = ["<Error: No gamma legs found>"];
    }
    return descs.join("\n");
  }

  protected createChildrenDescription(childOrders: Array<CubiqTS.Relationship>): string {
    if (!childOrders || childOrders.length == 0) return null;

    const siblingMap: Map<
      string,
      {
        desc: string;
        rel: CubiqTS.OrderRelationshipType;
        parentRel: CubiqTS.OrderRelationshipType;
      }
    > = new Map();
    const descriptions: Array<string> = [];
    let desc: string;

    // // Go through each child order, creating descriptions and moving OCO orders to separate map and array for future processing
    for (let child of childOrders) {
      if (child.order != null) {
        desc = this.createOrderDescription(child.order);
      } else {
        desc = this.createOrderIdDescription(child);
      }

      if (child.order && child.order.siblingRelationship) {
        siblingMap.set(child.orderId.toString(), {
          desc: desc,
          rel: child.order.siblingRelationship.relationshipType,
          parentRel: child.relationshipType,
        });
      } else {
        descriptions.push(CubiqTS.OrderRelationshipType[child.relationshipType] + "\n" + desc);
      }
    }

    // We now have array or all non-sibling orders and an array and associated map
    // of all child orders with sibling IDs

    siblingMap.forEach(
      (
        value: {
          desc: string;
          rel: CubiqTS.OrderRelationshipType;
          parentRel: CubiqTS.OrderRelationshipType;
        },
        _key: string,
      ) => {
        let compoundDesc: string =
          CubiqTS.OrderRelationshipType[value.parentRel] +
          " " +
          CubiqTS.OrderRelationshipType[value.rel] +
          "\n" +
          value.desc +
          "\n";
        // + siblingDesc;
        descriptions.push(compoundDesc);
      },
    );
    // // Now we create all sibling descriptions
    // for each(childOrder in siblingOrders) {
    //     // When OC is processed it will set processed flag on sibling order - we don't process these
    //     // as their descriptions have already been added
    //     if(!childOrder.isProcessed) {
    //         childOrder.isProcessed = true;

    //         order = childOrder.order;
    //         var siblingDesc:String;
    //         var siblingOrder:SiblingOrder = siblingMap[order.siblingID] as SiblingOrder;
    //         if(siblingOrder && !siblingOrder.isProcessed) {
    //             siblingOrder.isProcessed = true;
    //             siblingDesc = siblingOrder.description;
    //         }
    //         else {
    //             myLog.warn("Cannot find sibling order description for order " + order.siblingID);
    //             siblingDesc = "Order " + order.siblingID;
    //         }

    //         parentRelationship = formatter.formatOrderRelationshipType(order.parentRelationshipType);
    //         relationship = formatter.formatOrderRelationshipType(order.siblingRelationshipType);
    //         var compoundDesc:String = parentRelationship + " " + relationship + "\n"
    //             + childOrder.description + "\n"
    //             + siblingDesc;
    //         descriptions.push(compoundDesc);
    //     }
    // }
    return descriptions.join("\n");
  }

  protected getRequestOrderTypeLabel(order: CubiqTS.OrderDataImpl): string {
    let label = this.translate.instant(
      "RequestOrderType." + CubiqTS.RequestOrderType[order.requestedOrderType],
    );
    if (
      order.requestedOrderType == CubiqTS.RequestOrderType.CALL &&
      order.executionType == CubiqTS.ExecutionType.MANUAL
    ) {
      return this.sysConfigUtil.manualExecutionCallNameText;
    }

    return label;
  }

  protected clientIntent(order: CubiqTS.OrderDataImpl): string {
    switch (order.clientIntent) {
      case CubiqTS.OrderIntent.BUY:
        return "Buy";
      case CubiqTS.OrderIntent.SELL:
        return "Sell";
    }
    return "";
  }
}
